import axios from "axios";

/**
 * 🏛️ PLATFORM SERVICE: Centralized infrastructure control APIs.
 * This service handles communication with the Platform Governance layer.
 */

const getHeaders = () => {
  const user = JSON.parse(localStorage.getItem("platform_user") || "{}");
  return { Authorization: `Bearer ${user.token}` };
};

export const PlatformService = {
  /**
   * 📊 Fetch global platform metrics
   */
  getSummary: async () => {
    const res = await axios.get("/api/platform/summary", { headers: getHeaders() });
    return res.data;
  },

  /**
   * 🏘️ List all managed societies
   */
  listTenants: async (page = 1, limit = 20) => {
    const res = await axios.get(`/api/platform/tenants?page=${page}&limit=${limit}`, { headers: getHeaders() });
    return res.data;
  },

  /**
   * 🎭 Generate impersonation session
   */
  impersonate: async (tenantId: number) => {
    const res = await axios.post("/api/platform/support/impersonate", { tenantId }, { headers: getHeaders() });
    return res.data;
  },

  /**
   * 🛡️ Toggle society access (Suspension)
   */
  toggleSuspension: async (tenantId: number, isSuspended: boolean, reason: string) => {
    const res = await axios.post("/api/platform/tenants/suspension", { 
      tenantId, 
      isSuspended, 
      reason 
    }, { headers: getHeaders() });
    return res.data;
  },

  /**
   * 🛰️ Fetch recent platform events (Audit Trail)
   */
  getEvents: async (limit = 50) => {
    const res = await axios.get(`/api/platform/audit?limit=${limit}`, { headers: getHeaders() });
    return res.data;
  },

  /**
   * 🧠 Fetch AI-detected anomalies with filtering and pagination
   */
  getAnomalies: async (params: { page?: number; limit?: number; status?: string; severity?: string; tenantId?: number } = {}) => {
    const query = new URLSearchParams();
    if (params.page) query.append("page", params.page.toString());
    if (params.limit) query.append("limit", params.limit.toString());
    if (params.status) query.append("status", params.status);
    if (params.severity) query.append("severity", params.severity);
    if (params.tenantId) query.append("tenantId", params.tenantId.toString());

    const res = await axios.get(`/api/platform/anomalies?${query.toString()}`, { headers: getHeaders() });
    return res.data;
  },

  /**
   * 🔍 Fetch full details for a single tenant (drilldown)
   */
  getTenantDetails: async (id: number) => {
    const res = await axios.get(`/api/platform/tenants/${id}`, { headers: getHeaders() });
    return res.data;
  },

  /**
   * ⏱️ Fetch chronological activity timeline for a tenant
   */
  getTenantActivity: async (id: number) => {
    const res = await axios.get(`/api/platform/tenants/${id}/activity`, { headers: getHeaders() });
    return res.data;
  },

  /**
   * 🔐 Fetch cross-tenant login event feed
   */
  getLoginLogs: async (page = 1, limit = 30, search?: string, tenantId?: number) => {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (search)   params.append("search",   search);
    if (tenantId) params.append("tenantId", String(tenantId));
    const res = await axios.get(`/api/platform/security/logins?${params}`, { headers: getHeaders() });
    return res.data;
  },

  /**
   * 🛡️ Fetch cross-tenant permission/role change audit logs
   */
  getPermissionAuditLogs: async (page = 1, limit = 30) => {
    const res = await axios.get(`/api/platform/security/permissions?page=${page}&limit=${limit}`, { headers: getHeaders() });
    return res.data;
  },

  /**
   * 🎭 Fetch impersonation + admin action log
   */
  getImpersonationLogs: async (page = 1, limit = 30) => {
    const res = await axios.get(`/api/platform/security/impersonation?page=${page}&limit=${limit}`, { headers: getHeaders() });
    return res.data;
  },

  /**
   * 🛡️ Fetch subscription lifecycle report (Expiring plans, health, etc.)
   */
  getSubscriptions: async () => {
    const res = await axios.get("/api/platform/subscriptions", { headers: getHeaders() });
    return res.data;
  },

  /**
   * 📊 Portfolio health report — bulk health scores for all tenants
   */
  getPortfolioReport: async () => {
    const res = await axios.get("/api/platform/reports/portfolio", { headers: getHeaders() });
    return res.data;
  },

  /**
   * 💰 Revenue report — MRR/ARR, billing collections, top tenants
   */
  getRevenueReport: async () => {
    const res = await axios.get("/api/platform/reports/revenue", { headers: getHeaders() });
    return res.data;
  },

  /**
   * 🛡️ Security summary — aggregated login, impersonation and role change metrics
   */
  getSecuritySummary: async () => {
    const res = await axios.get("/api/platform/reports/security-summary", { headers: getHeaders() });
    return res.data;
  },

  /**
   * 🔬 OCR intelligence — scan quality, accuracy and correction rates across all tenants
   */
  getOcrReport: async () => {
    const res = await axios.get("/api/platform/reports/ocr", { headers: getHeaders() });
    return res.data;
  },

  /**
   * 💓 Subsystem heartbeat — API, DB, WA, OCR, Queue health (polled every 60s)
   */
  getHealth: async () => {
    const res = await axios.get("/api/platform/health", { headers: getHeaders() });
    return res.data;
  }
};
